import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Spinner } from "@/components/shared";
import { API_BASE } from "@/lib/api";

const META = {
  privacy: { title: "Privacy Policy" },
  terms: { title: "Terms of Use" },
  support: { title: "Support" },
};

export default function Legal({ doc }) {
  const [html, setHtml] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const canonical = `${API_BASE}/legal/${doc}`;
  const label = META[doc]?.title || "Legal";

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    document.title = `Amoreska — ${label}`;
    (async () => {
      try {
        const res = await fetch(canonical);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const parsed = new DOMParser().parseFromString(text, "text/html");
        parsed.querySelectorAll("style, script, iframe, object, embed").forEach((n) => n.remove());
        // Strip inline event-handler attributes as a light sanitization pass.
        parsed.body.querySelectorAll("*").forEach((el) => {
          [...el.attributes].forEach((a) => {
            if (/^on/i.test(a.name)) el.removeAttribute(a.name);
          });
        });
        // Remove the document's own brand <h1>Amoreska</h1> ...
        const firstH1 = parsed.body.querySelector("h1");
        if (firstH1 && /amoreska/i.test(firstH1.textContent || "")) firstH1.remove();
        // ... and its duplicate page-title heading (we render our own branded title).
        const titleHeading = parsed.body.querySelector("h1, h2");
        if (titleHeading) titleHeading.remove();
        if (alive) {
          setHtml(parsed.body.innerHTML);
          setStatus("ok");
        }
      } catch (e) {
        if (alive) setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [doc, canonical, label]);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border/50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-home-logo">
            <Logo size={36} />
            <span className="font-display text-xl tracking-tight">Amoreska</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16" data-testid={`legal-${doc}`}>
        <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
          Legal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-8" data-testid="legal-title">
          {label}
        </h1>

        {status === "loading" && <Spinner />}

        {status === "ok" && (
          <div
            className="legal-prose"
            data-testid="legal-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}

        {status === "error" && (
          <div className="rounded-2xl bg-card ring-1 ring-border/70 p-6" data-testid="legal-error">
            <p className="text-muted-foreground">
              We couldn't load this page inline. You can open the official version directly:
            </p>
            <a
              href={canonical}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-primary font-semibold hover:underline"
              data-testid="legal-fallback-link"
            >
              Open {label} <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}
      </main>

      <footer className="border-t border-border/50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-foreground transition-colors">Support</Link>
          </div>
          <span>© 2026 Amoreska.</span>
        </div>
      </footer>
    </div>
  );
}
